import type { FormFieldDescriptor, FormMode, TranslationKey } from '@finance/contracts';
import { evaluateCondition, type EvaluationContext } from '../conditions/evaluate-condition';

export interface ResolvedFieldState {
  /** One mutually-exclusive interaction state for every field adapter. */
  interaction: FieldInteractionState;
  visible: boolean;
  editable: boolean;
  readonly: boolean;
  disabled: boolean;
  required: boolean;
  masked: boolean;
  accessDenied: boolean;
  reasonKey?: TranslationKey;
  decisionId?: string;
  policyVersion?: string;
}

/** Inputs which are not form data, but alter a control's interaction state. */
export interface FieldStateResolutionOptions {
  /** A request is in flight. The value remains visible but must not be edited. */
  pending?: boolean;
  /** A resolved server projection may explicitly retain a denied placeholder. */
  accessDenied?: boolean;
  /** A resolved server projection may retain a visible masked value. */
  masked?: boolean;
}

/**
 * Canonical adapter state model. `readonly` is a presentation state, while
 * business disablement and temporary pending retain their distinct meanings.
 */
export type FieldInteractionState =
  | 'hidden'
  | 'readonly'
  | 'disabled-by-business-state'
  | 'temporarily-pending'
  | 'editable'
  | 'access-denied';

export function resolveFieldState(
  field: FormFieldDescriptor,
  mode: FormMode,
  values: Record<string, unknown>,
  ctx: EvaluationContext,
  options: FieldStateResolutionOptions = {},
): ResolvedFieldState {
  const isViewMode = mode === 'view';
  // P0-4: Server-authoritative field access. The server's resolveField()
  // already filters fields by view/edit operation access and applies
  // deniedBehavior (hide/readonly/masked). The client only needs to evaluate
  // visibility/readonly/disabled conditions on the fields the server returned.
  const accessDenied = options.accessDenied === true;
  // Resolved descriptors normally omit unauthorized fields. If a caller
  // deliberately retains a denial placeholder, keep it visible so the layout
  // can render the safe denied-state component rather than silently omitting
  // it a second time.
  const visible = accessDenied || resolveVisibility(field, values, ctx, mode);
  const readonly = !accessDenied && (isViewMode || resolveReadonly(field, values, ctx));
  const businessDisabled = !accessDenied && !readonly && resolveDisabled(field, values, ctx);
  const temporarilyPending = !accessDenied && !readonly && !businessDisabled && options.pending === true;
  const disabled = readonly || businessDisabled || temporarilyPending || accessDenied;
  const editable = visible && !readonly && !disabled;
  const required = resolveRequired(field, values, ctx) && visible && editable;
  const interaction: FieldInteractionState = accessDenied
    ? 'access-denied'
    : !visible
    ? 'hidden'
    : readonly
      ? 'readonly'
      : businessDisabled
        ? 'disabled-by-business-state'
        : temporarilyPending
          ? 'temporarily-pending'
        : 'editable';

  return {
    interaction,
    visible,
    editable,
    readonly,
    disabled,
    required,
    masked: options.masked === true,
    accessDenied,
  };
}

function resolveVisibility(
  field: FormFieldDescriptor,
  values: Record<string, unknown>,
  ctx: EvaluationContext,
  mode: FormMode,
): boolean {
  const condition = field.state?.visibilityCondition;
  return condition ? evaluateCondition(condition, { ...ctx, data: values }) : true;
}

function resolveReadonly(field: FormFieldDescriptor, values: Record<string, unknown>, ctx: EvaluationContext): boolean {
  const condition = field.state?.readonlyCondition;
  return condition ? evaluateCondition(condition, { ...ctx, data: values }) : false;
}

function resolveRequired(field: FormFieldDescriptor, values: Record<string, unknown>, ctx: EvaluationContext): boolean {
  if (field.validation?.rules?.some((rule) => String(rule.ruleId) === 'required')) return true;
  const condition = field.state?.requiredCondition;
  return condition ? evaluateCondition(condition, { ...ctx, data: values }) : false;
}

function resolveDisabled(field: FormFieldDescriptor, values: Record<string, unknown>, ctx: EvaluationContext): boolean {
  const condition = field.state?.disabledCondition;
  return condition ? evaluateCondition(condition, { ...ctx, data: values }) : false;
}
